import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { doc, setDoc, collection, query, where, getDocs, updateDoc, Timestamp, serverTimestamp, addDoc } from 'firebase/firestore';
import { db } from '../../config/firebase';

const VAPID_KEY = 'VOTRE_CLE_VAPID_PUBLIQUE';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'danger';
  createdAt: Date;
  read: boolean;
  weddingId: string;
}

export const notificationsService = {
  async requestPermission(userId: string) {
    try {
      const messaging = getMessaging();
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        const token = await getToken(messaging, { vapidKey: VAPID_KEY });
        
        // Sauvegarder le token dans Firestore
        await setDoc(doc(db, 'users', userId), {
          fcmTokens: [token],
          notificationsEnabled: true
        }, { merge: true });

        return token;
      }
      
      return null;
    } catch (error) {
      console.error('Erreur lors de la demande de permission:', error);
      throw error;
    }
  },

  async setupMessageListener(callback: (payload: any) => void) {
    try {
      const messaging = getMessaging();
      
      onMessage(messaging, (payload) => {
        // Créer une notification native si l'app est ouverte
        if (Notification.permission === 'granted' && payload.notification) {
          const title = payload.notification.title || 'Nouvelle notification';
          const body = payload.notification.body || '';
          new Notification(title, {
            body,
            icon: '/logo192.png'
          });
        }
        
        callback(payload);
      });
    } catch (error) {
      console.error('Erreur lors de la configuration du listener:', error);
    }
  },

  async getNotifications(weddingId: string): Promise<Notification[]> {
    try {
      // // // // // // // // // // // // console.log('[DEBUG] Récupération des notifications pour le mariage:', weddingId);
      const notificationsRef = collection(db, 'notifications');
      const q = query(
        notificationsRef, 
        where('weddingId', '==', weddingId),
        where('createdAt', '!=', null)
      );
      
      const querySnapshot = await getDocs(q);
      // // // // // // // // // // // // console.log('[DEBUG] Nombre de notifications trouvées:', querySnapshot.size);
      
      return querySnapshot.docs.map(doc => {
        const data = doc.data();
        // // // // // // // // // // // // console.log('[DEBUG] Données de la notification:', { id: doc.id, data });
        
        // Vérifier si createdAt existe et est un Timestamp
        let createdAt = new Date();
        if (data['createdAt']) {
          if (data['createdAt'] instanceof Timestamp) {
            createdAt = data['createdAt'].toDate();
          } else if (typeof data['createdAt'].toDate === 'function') {
            createdAt = data['createdAt'].toDate();
          }
        }

        return {
          id: doc.id,
          ...data,
          createdAt
        } as Notification;
      });
    } catch (error) {
      console.error('[DEBUG] Erreur lors de la récupération des notifications:', error);
      return [];
    }
  },

  async createNotification(notification: Omit<Notification, 'id' | 'createdAt'>): Promise<string> {
    try {
      const notificationData = {
        ...notification,
        createdAt: serverTimestamp(),
        read: false
      };

      const docRef = await addDoc(collection(db, 'notifications'), notificationData);
      return docRef.id;
    } catch (error) {
      console.error('Erreur lors de la création de la notification:', error);
      throw error;
    }
  },

  async markAsRead(notificationId: string): Promise<void> {
    try {
      const notificationRef = doc(db, 'notifications', notificationId);
      await updateDoc(notificationRef, {
        read: true,
        updatedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Erreur lors du marquage de la notification comme lue:', error);
      throw error;
    }
  }
}; 